// Datenzugriff der Freelancer-Gutschriften (ERP/CRM-Plan, Abschnitt 7
// „Workflow" Schritt 5 — Phase D5).
//
// Unrein: hier liegen ausschliesslich Supabase-Aufrufe, das
// snake_case<->camelCase-Mapping und die PDF-Ablage (Stil wie
// assignmentService.ts: async, throw bei error, Mapper oben). Die Fachlichkeit
// — Betraege, Statusuebergaenge, Abrechenbarkeit, Faelligkeit — steht pur und
// getestet in freelancerCreditNoteLogic.ts und wird von hier nur AUFGERUFEN,
// nicht nachgebaut.
//
// ─── Schreiben ausschliesslich ueber RPCs ───────────────────────────────────
// Es gibt hier keinen einzigen insert/update/delete auf den beiden Tabellen.
// Die permissive Policy steht auf `for select` (supabase/
// freelancer_credit_notes.sql, Abschnitt 4a) — ein Direkt-Schreibweg
// existiert auch fuer den Innendienst nicht. Jede Aenderung laeuft ueber eine
// SECURITY-DEFINER-RPC:
//
//   create_credit_note_run  — Monatslauf, buendelt geprueft-Auftraege
//   issue_credit_note       — Nummer + Snapshot + Auftraege auf abgerechnet
//   cancel_credit_note      — Storno-Beleg, Auftraege zurueck auf geprueft
//   mark_credit_note_paid   — Zahlungsausgang
//   mark_credit_note_sent   — Versandvermerk
//   set_credit_note_pdf     — Ablagepfad nach dem Upload
//
// Tabellen, RLS und RPCs: supabase/freelancer_credit_notes.sql
// (MIGRATIONS.md Nr. 22).

import { supabase } from './supabaseClient';
import { describeError } from './errorMessage';
import { InspectionAssignment } from './assignmentLogic';
import { fetchAssignments, fetchProjektKoepfe } from './assignmentService';
import {
    FreelancerCreditNote,
    FreelancerCreditNoteLine,
    LaufVorschau,
    laufVorschau,
} from './freelancerCreditNoteLogic';

/**
 * Ablage der Beleg-PDFs.
 *
 * Bestands-Bucket `invoices` (MIGRATIONS.md #1), eigener Ordner. Ein zweiter
 * Bucket waere eine zweite Ablage mit zweitem Policy-Satz fuer dieselbe Sorte
 * Dokument — beides sind aufbewahrungspflichtige Belege nach §147 AO.
 *
 * Der Bucket ist ADMIN-ONLY. Ein Freelancer kann sein eigenes PDF deshalb
 * NICHT herunterladen; das Portal weist darauf hin und nennt den Weg
 * (E-Mail). Ausfuehrliche Begruendung: freelancer_credit_notes.sql,
 * Abschnitt 7.
 */
export const CREDIT_NOTE_BUCKET = 'invoices';
export const CREDIT_NOTE_ORDNER = 'gutschriften';

// ─── Row-Mapper ──────────────────────────────────────────────────────────────

const mapCreditNoteRow = (row: any): FreelancerCreditNote => ({
    id: String(row.id),
    companyId: String(row.company_id ?? ''),
    freelancerId: String(row.freelancer_id),
    nummer: row.nummer ?? undefined,
    // jsonb kommt als Objekt zurueck; ein NULL-Snapshot waere ein Entwurf.
    freelancerSnapshot: (row.freelancer_snapshot as Record<string, unknown>) ?? {},
    // int aus PostgREST kommt als Zahl, ueber numeric-Umwege aber als String —
    // Number() haelt den Cent-Betrag verlaesslich rechenbar.
    nettoCents: Number(row.netto_cents ?? 0),
    ustCents: Number(row.ust_cents ?? 0),
    bruttoCents: Number(row.brutto_cents ?? 0),
    steuersatzBp: Number(row.steuersatz_bp ?? 1900),
    leistungszeitraumVon: row.leistungszeitraum_von ?? undefined,
    leistungszeitraumBis: row.leistungszeitraum_bis ?? undefined,
    status: row.status,
    stornoOf: row.storno_of ?? undefined,
    stornoGrund: row.storno_grund ?? undefined,
    paidAt: row.paid_at ?? undefined,
    zahlungszielTage: Number(row.zahlungsziel_tage ?? 14),
    pdfPfad: row.pdf_pfad ?? undefined,
    ausgestelltAm: row.ausgestellt_am ?? undefined,
    createdAt: row.created_at,
});

const mapLineRow = (row: any): FreelancerCreditNoteLine => ({
    id: String(row.id),
    creditNoteId: String(row.credit_note_id),
    assignmentId: String(row.assignment_id),
    descriptionSnapshot: String(row.description_snapshot ?? ''),
    serviceDate: row.service_date,
    netCents: Number(row.net_cents ?? 0),
    voidedAt: row.voided_at ?? undefined,
});

// ─── Lesen ───────────────────────────────────────────────────────────────────

export interface CreditNoteQuery {
    freelancerId?: string;
    /** Ausstellungsdatum ab/bis (ISO). Fuer den DATEV-Export nach Zeitraum. */
    von?: string;
    bis?: string;
}

/**
 * Belegliste.
 *
 * Bewusst OHNE Statusfilter: Entwuerfe, ausgestellte, bezahlte und stornierte
 * Belege stehen in EINER Liste. Ein Entwurf, der erst nach dem Umschalten
 * eines Filters sichtbar wird, wird vergessen — und ein vergessener Entwurf
 * ist eine nicht bezahlte Begehung.
 */
export const fetchCreditNotes = async (
    q: CreditNoteQuery = {},
): Promise<FreelancerCreditNote[]> => {
    let query = supabase
        .from('freelancer_credit_notes')
        .select('*')
        .order('created_at', { ascending: false });
    if (q.freelancerId) query = query.eq('freelancer_id', q.freelancerId);
    // Der Zeitraum bezieht sich auf das AUSSTELLUNGSdatum, nicht auf die
    // Leistung: der Steuerberater bucht nach Belegdatum.
    if (q.von) query = query.gte('ausgestellt_am', q.von);
    if (q.bis) query = query.lte('ausgestellt_am', q.bis);

    const { data, error } = await query;
    if (error) throw new Error(describeError(error));
    return (data || []).map(mapCreditNoteRow);
};

export const fetchCreditNote = async (id: string): Promise<FreelancerCreditNote | null> => {
    if (!id) return null;
    const { data, error } = await supabase
        .from('freelancer_credit_notes')
        .select('*')
        .eq('id', id)
        .maybeSingle();
    if (error) throw new Error(describeError(error));
    return data ? mapCreditNoteRow(data) : null;
};

/** Die Positionen EINES Belegs, in Belegreihenfolge (aeltestes Datum zuerst). */
export const fetchCreditNoteLines = async (
    creditNoteId: string,
): Promise<FreelancerCreditNoteLine[]> => {
    if (!creditNoteId) return [];
    const { data, error } = await supabase
        .from('freelancer_credit_note_lines')
        .select('*')
        .eq('credit_note_id', creditNoteId)
        .order('service_date', { ascending: true });
    if (error) throw new Error(describeError(error));
    return (data || []).map(mapLineRow);
};

/** Positionen zu MEHREREN Belegen auf einmal — keine Abfrage je Zeile. */
export const fetchCreditNoteLinesFor = async (
    creditNoteIds: string[],
): Promise<Map<string, FreelancerCreditNoteLine[]>> => {
    const eindeutig = Array.from(new Set((creditNoteIds || []).filter(Boolean)));
    if (eindeutig.length === 0) return new Map();

    const { data, error } = await supabase
        .from('freelancer_credit_note_lines')
        .select('*')
        .in('credit_note_id', eindeutig)
        .order('service_date', { ascending: true });
    if (error) throw new Error(describeError(error));

    const map = new Map<string, FreelancerCreditNoteLine[]>();
    for (const row of (data || []) as any[]) {
        const line = mapLineRow(row);
        const liste = map.get(line.creditNoteId);
        if (liste) liste.push(line);
        else map.set(line.creditNoteId, [line]);
    }
    return map;
};

// ─── Vorschau des Monatslaufs ────────────────────────────────────────────────

export interface LaufVorschauErgebnis extends LaufVorschau {
    /** Projektnamen zu den Auftraegen — die Vorschau zeigt Projekte, nicht uuids. */
    projektNamen: Map<string, string>;
}

/**
 * Was wuerde der Lauf buendeln?
 *
 * Drei Abfragen: die geprueften Auftraege des Freelancers, die bereits
 * gebundenen assignment_ids und die Projektnamen. Die Auswahl selbst trifft
 * `laufVorschau` (pur, getestet) — dieselbe Regel, die die RPC serverseitig
 * anwendet.
 *
 * Die BEREITS GEBUNDENEN Zeilen werden ausdruecklich mitgeladen: entwertete
 * Zeilen (Storno) zaehlen nicht, und genau diese Unterscheidung ist der Grund
 * fuer den partiellen Unique-Index in der Datenbank. Ein Client, der sie nicht
 * kennt, zeigte nach einem Storno eine leere Vorschau an, obwohl der Lauf
 * durchliefe.
 */
export const ladeLaufVorschau = async (
    freelancerId: string,
    bis: string,
    steuersatzBp: number,
): Promise<LaufVorschauErgebnis> => {
    if (!freelancerId) {
        return { assignments: [], betraege: { nettoCents: 0, ustCents: 0, bruttoCents: 0 }, projektNamen: new Map() };
    }

    const geprueft: InspectionAssignment[] = await fetchAssignments({
        freelancerId,
        status: ['geprueft'],
    });

    const { data, error } = await supabase
        .from('freelancer_credit_note_lines')
        .select('assignment_id, voided_at')
        .is('voided_at', null);
    if (error) throw new Error(describeError(error));
    const gebunden = (data || []).map((r: any) => String(r.assignment_id));

    const vorschau = laufVorschau(geprueft, steuersatzBp, {
        freelancerId,
        bis,
        bereitsAbgerechnet: gebunden,
    });

    const koepfe = await fetchProjektKoepfe(vorschau.assignments.map((a) => a.projectId));
    const projektNamen = new Map<string, string>();
    for (const [id, kopf] of koepfe) projektNamen.set(id, kopf.name);

    return { ...vorschau, projektNamen };
};

// ─── Schreiben (RPC) ─────────────────────────────────────────────────────────

/**
 * Monatslauf: Entwurf mit Positionen anlegen.
 *
 * Rueckgabe ist der angelegte Beleg, nicht nur seine id — die aufrufende
 * Liste soll ihn ohne zweite Rundreise anzeigen koennen.
 */
export const createCreditNoteRun = async (
    freelancerId: string,
    bis: string,
): Promise<FreelancerCreditNote> => {
    const { data, error } = await supabase.rpc('create_credit_note_run', {
        p_freelancer: freelancerId,
        p_bis: bis,
    });
    if (error) throw new Error(describeError(error));

    const id = String(data ?? '');
    const angelegt = id ? await fetchCreditNote(id) : null;
    if (!angelegt) {
        throw new Error(
            'Die Gutschrift wurde angelegt, liess sich aber nicht laden. Bitte neu laden.',
        );
    }
    return angelegt;
};

/**
 * Entwurf verwerfen.
 *
 * Ueber die RPC und NICHT per direktem DELETE: die permissive Policy steht auf
 * `for select`, ein Client-DELETE traefe nie eine Zeile und scheiterte still.
 */
export const deleteCreditNoteDraft = async (id: string): Promise<void> => {
    const { error } = await supabase.rpc('delete_credit_note_draft', { p_id: id });
    if (error) throw new Error(describeError(error));
};

/** Ausstellen. Rueckgabe ist die gezogene Belegnummer. */
export const issueCreditNote = async (id: string): Promise<string> => {
    const { data, error } = await supabase.rpc('issue_credit_note', { p_id: id });
    if (error) throw new Error(describeError(error));
    return String(data ?? '');
};

/**
 * Stornieren. Rueckgabe ist der NEUE Storno-Beleg — die Liste soll ihn sofort
 * zeigen, denn er ist das Ergebnis der Handlung.
 */
export const cancelCreditNote = async (
    id: string,
    grund: string,
): Promise<FreelancerCreditNote | null> => {
    const { data, error } = await supabase.rpc('cancel_credit_note', {
        p_id: id,
        p_grund: (grund || '').trim(),
    });
    if (error) throw new Error(describeError(error));
    const stornoId = String(data ?? '');
    return stornoId ? fetchCreditNote(stornoId) : null;
};

export const markCreditNotePaid = async (id: string, paidAt: string): Promise<void> => {
    const { error } = await supabase.rpc('mark_credit_note_paid', {
        p_id: id,
        p_paid_at: paidAt || null,
    });
    if (error) throw new Error(describeError(error));
};

export const markCreditNoteSent = async (id: string): Promise<void> => {
    const { error } = await supabase.rpc('mark_credit_note_sent', { p_id: id });
    if (error) throw new Error(describeError(error));
};

// ─── PDF-Ablage ──────────────────────────────────────────────────────────────

/** Ablagepfad: gutschriften/<jahr>/<nummer>.pdf — analog zum Rechnungsarchiv. */
export const creditNotePdfPfad = (nummer: string, ausgestelltAm?: string): string => {
    const jahr = (ausgestelltAm || '').slice(0, 4) || String(new Date().getFullYear());
    // Der Nummernkreis liefert nur Buchstaben, Ziffern und Bindestrich; die
    // Bereinigung ist der Gurt fuer den Fall, dass sich das je aendert.
    const sicher = nummer.replace(/[^A-Za-z0-9._-]/g, '_');
    return `${CREDIT_NOTE_ORDNER}/${jahr}/${sicher}.pdf`;
};

/**
 * Beleg-PDF ablegen und den Pfad am Beleg vermerken.
 *
 * `upsert: true` — anders als beim Rechnungsarchiv (#1, dort MUSS ein zweiter
 * Schreibversuch scheitern, weil die GoBD-Unveraenderbarkeit daran haengt).
 * Hier gibt es keinen Hash-Snapshot, an dem sich ein zweiter Upload messen
 * liesse; der Beleg selbst ist ueber den Freeze-Trigger unveraenderlich, und
 * ein erneut erzeugtes PDF aus denselben, eingefrorenen Daten ist Zeichen fuer
 * Zeichen dasselbe Dokument. Ein Fehlschlag beim ersten Versuch (Netz) waere
 * sonst eine Sackgasse.
 */
export const speichereCreditNotePdf = async (
    beleg: Pick<FreelancerCreditNote, 'id' | 'nummer' | 'ausgestelltAm'>,
    bytes: ArrayBuffer | Uint8Array,
): Promise<string> => {
    if (!beleg.nummer) {
        throw new Error('Ein Entwurf hat keine Nummer — bitte zuerst ausstellen.');
    }
    const pfad = creditNotePdfPfad(beleg.nummer, beleg.ausgestelltAm);

    const { error: upErr } = await supabase.storage
        .from(CREDIT_NOTE_BUCKET)
        .upload(pfad, new Blob([bytes as BlobPart], { type: 'application/pdf' }), {
            upsert: true,
            contentType: 'application/pdf',
        });
    if (upErr) throw new Error(describeError(upErr));

    const { error } = await supabase.rpc('set_credit_note_pdf', { p_id: beleg.id, p_pfad: pfad });
    if (error) {
        // Der Verweis konnte nicht gesetzt werden — die Datei waere sonst
        // unauffindbar im Bucket. Best-effort wieder entfernen (Muster
        // freelancerService.uploadQualificationDocument).
        await supabase.storage.from(CREDIT_NOTE_BUCKET).remove([pfad]).then(undefined, () => {
            /* egal */
        });
        throw new Error(describeError(error));
    }
    return pfad;
};

/**
 * Kurzlebige signierte URL zum Ansehen/Herunterladen.
 *
 * Nur fuer den Innendienst: der Bucket ist admin-only. Fuer den Freelancer
 * gibt es diesen Weg BEWUSST nicht (Begruendung oben bei CREDIT_NOTE_BUCKET).
 */
export const getCreditNotePdfUrl = async (
    pfad: string,
    expiresInSeconds = 300,
): Promise<string> => {
    if (!pfad) throw new Error('Zu diesem Beleg ist kein PDF hinterlegt.');
    const { data, error } = await supabase.storage
        .from(CREDIT_NOTE_BUCKET)
        .createSignedUrl(pfad, expiresInSeconds);
    if (error) throw new Error(describeError(error));
    const url = data?.signedUrl ?? '';
    if (!url) throw new Error('Das PDF ließ sich nicht öffnen.');
    return url;
};

// ─── Portal-Sicht ────────────────────────────────────────────────────────────

/**
 * Die EIGENEN Belege des angemeldeten Freelancers.
 *
 * Ohne Client-Filter auf „meine" Zeilen — das waere ein Sicherheitsversprechen,
 * das ein Client nicht halten kann. Die Begrenzung leistet die RLS
 * (freelancer_credit_notes.sql, Abschnitt 4b): sichtbar sind ausschliesslich
 * die eigenen, AUSGESTELLTEN Belege. Ein Entwurf ist interner Arbeitsstand und
 * bleibt unsichtbar.
 */
export const fetchMeineGutschriften = async (): Promise<FreelancerCreditNote[]> => {
    const { data, error } = await supabase
        .from('freelancer_credit_notes')
        .select('*')
        .order('created_at', { ascending: false });
    if (error) throw new Error(describeError(error));
    return (data || []).map(mapCreditNoteRow);
};
